/**
 * The rules every upload obeys — timeout, backoff, and what counts as final.
 *
 * These used to live in `vaultUploadQueue`, which was the older of the app's
 * two queues and therefore the accidental home for policy that belonged to
 * both. FEAT-028 wave 4 folded that queue into `sendOutbox` (the pipeline
 * contract: uploads are ONE client service), so the policy moved here — a
 * module with no queue in it, that anything may import without an import cycle.
 *
 * Every number below was paid for by a bug. Change them deliberately.
 */

/** 60s base + ~1s per 32KB, capped at 10 minutes — a 35MB video gets the cap
 * instead of the old flat 30s that weak signal could never satisfy (BUG-048). */
export function computeUploadTimeoutMs(sizeBytes: number): number {
  return Math.min(60_000 + sizeBytes / 32, 600_000);
}

const RETRY_LADDER_MS = [5_000, 30_000, 120_000, 300_000, 900_000];

export function retryDelayMs(retryCount: number): number {
  const index = Math.min(Math.max(retryCount - 1, 0), RETRY_LADDER_MS.length - 1);
  return RETRY_LADDER_MS[index];
}

/** 4xx = the server understood and said no — retrying is pointless.
 * 408/429 are the transient exceptions, and so is 401: "you're signed out"
 * means "sign in and this will work", not "this can never work" (BUG-069).
 *
 * BUG-110: the save queue never had this rule, so a save the server would
 * never accept burned five retries and parked as 'stuck' forever, with a card
 * the user had to dismiss by hand on every screen. */
export function isPermanentStatus(status: number | undefined): boolean {
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 401 &&
    status !== 408 &&
    status !== 429
  );
}

/** Five straight failures and a row stops retrying by itself: it becomes a
 * visible decision — Try again or Discard — instead of rotting invisibly.
 * The user decides; the app never deletes their moment for them (BUG-048). */
export const STUCK_AFTER_FAILURES = 5;

// ---- Chunked resumable uploads (FEAT-028 wave 5) ----

/** 2 MiB: at one-bar 0.5 Mbps a chunk is ~32s — safely under the ~100s proxy
 * ceiling that BUG-055 measured killing whole-file sends. Mean lost work per
 * iOS background-kill is half a chunk. Server rejects anything over 8 MiB. */
export const CHUNK_BYTES = 2 * 1024 * 1024;

/** Chunk only at/above 6 MiB. Photos (~1.2MB full-res JPEG) keep the proven
 * single-POST path (~200ms) with zero extra round trips; any video longer
 * than ~8s at the 6 Mbps ask exceeds this, and a chunked upload is always
 * ≥3 chunks so the protocol overhead never dominates the transfer. */
export const CHUNK_THRESHOLD_BYTES = 6 * 1024 * 1024;

/** Per-chunk attempt bound. A chunk must fail FAST into the resume loop —
 * never inherit the whole-file formula's 10-minute cap, which would turn one
 * dead chunk into the exact invisible stall this wave exists to end. */
export function computeChunkTimeoutMs(sizeBytes: number): number {
  return 30_000 + sizeBytes / 32;
}

/** Three quick foreground retries per chunk, then the entry fails into the
 * existing fallback (bytes-in-the-claim → outbox). Failures while the app is
 * hidden don't burn these — that's iOS killing the transfer, and the answer
 * is "resume when visible", not "give up". */
export const CHUNK_RETRY_LADDER_MS = [2_000, 5_000, 15_000];
